import Aegis from '@tencent/aegis-web-sdk-v2';
import { config } from 'lib/config';
import channelService from 'lib/services/channelService';
import { ENV_TYPE } from 'lib/constants/login';
import { getLoginType, getOrientation } from '../util';
import TRACE_ENUM from './trace-enum';

const url = 'https://galileotelemetry.tencent.com/collect';
const id = 'SDK-868da8e9c74497db69d7';

export { TRACE_ENUM };

/**
 * 日志全量上报，需要注意的是，上报事如果发生了页面切换，数据可能无法上报
 * @param actionName 事件名
 * @param extObj 上报的参数
 * @param force 是否在不满足数据校验的情况下强制上报
 */

interface ITraceReportMsg {
  actionName: string;
  [key: string]: any;
}

class Monitor {
  private monitor;
  private gameWithEnvTag;

  public init() {
    this.monitor = new Aegis({
      id,
      hostUrl: { url },
      env: this.getEnv(),
      delay: 2000,
      extField: {
        ...this.getExtMonitorConfig(),
      },
      plugin: {
        device: true, // 默认开启device收集器
        close: true, // 默认开启close收集器
        aid: true, // 默认开启aid收集器
        fId: false, // 默认关闭fingerId
        ie: false, // 默认关闭ie收集器
        // 页面访问
        pv: true, // 默认开启pv收集器
        spa: true, // 默认开启spa收集器
        // 勾选异常分析
        error: true, // 默认开启error收集器
        // 勾选静态资源监控
        assetSpeed: true, // 默认开启assetSpeed收集器
        // apiSpeed: true, // 官方文档没有写该配置，api应该是默认开启的
        // 勾选页面性能
        pagePerformance: true, // 默认开启pagePerformance收集器
        webVitals: true, // 默认开启webVitals收集器
        // 勾选session功能
        session: true,
        // 同时勾选api和trace或者仅勾选trace
        api: {
          apiDetail: true,
          injectTraceHeader: 'traceparent', // session串联后端
          injectTraceUrls: [], // session串联后端
          resHeaders: [
            'Requestid',
            'requestid',
            'loginType',
            'serviceType',
            'msdkVersion',
            'Authorization',
            'x-galileo-trace-id',
          ],
          retCodeHandler(data) {
            try {
              const resData = JSON.parse(data);
              return {
                isErr: resData.code !== 0 && resData.code !== 200,
                code: resData.code,
              };
            } catch (e) {
              return {
                isErr: true,
                code: data,
              };
            }
          },
        },
      },
    });
  }

  public setMonitorConfig(uid) {
    this.monitor.setConfig({
      uid,
      extField: { ...this.getExtMonitorConfig() },
    });
  }

  public reportEvent(name: string, data?: string) {
    try {
      this.monitor.reportEvent({
        name,
        ext3: data,
      });
    } catch (e) {
      console.log('reportEvent error', e);
    }
  }

  public reportLog(msg: ITraceReportMsg) {
    if (!msg.actionName) {
      console.log('reportLog error: actionName is required');
      return;
    }

    try {
      const reportMsg = {
        ...msg,
        // RUM的检索只能匹配一个关键词，因此和游戏、环境等相关信息拼接成searchKey
        searchKey: this.generateSearchKey(msg.actionName),
      };
      this.monitor.info({
        msg: JSON.stringify(reportMsg),
      });
    } catch (e) {
      console.log('reportLog error', e);
    }
  }

  private generateSearchKey(action) {
    return `${this.gameWithEnvTag}-${action}`;
  }

  private getExtMonitorConfig() {
    const { gameCode } = channelService;
    const loginType = getLoginType();
    this.gameWithEnvTag = `${gameCode}-${loginType === ENV_TYPE.MSDK ? 'msdk' : 'nonmsdk'}`;
    return {
      ext1: this.gameWithEnvTag,
      ext2: getOrientation(),
    };
  }

  private getEnv() {
    switch (config.env) {
      case 'test':
        return Aegis.environment.TEST;
      case 'gray':
        return Aegis.environment.GRAY;
      case 'prod':
      default:
        return Aegis.environment.PROD;
    }
  }
}

const monitor = new Monitor();

export default monitor;
